import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, CalendarClock, Loader2, AlertTriangle, MapPin } from "lucide-react";
import { api, apiErr } from "../api";
import { BRAND } from "../brand";
import { PanelPage, BrandLogo, prettyDate, todayISO } from "../ui";

/** Página pública que recibe el paciente por email/WhatsApp para confirmar o pedir otra fecha. */
export default function PatientConfirm() {
  const { token } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState(null);
  const [fecha, setFecha] = useState("");
  const [nota, setNota] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");

  useEffect(() => {
    api.get(`/public/appointment/${token}`).then((r) => setA(r.data)).catch((e) => setErr(apiErr(e, "No encontramos esta cita.")));
  }, [token]);

  const respond = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/appointment/${token}/respond`, { action, requested_fecha: fecha, nota });
      setDone(data.status);
    } catch (e) { setErr(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <PanelPage>
      <div className="min-h-screen flex flex-col items-center px-4 py-10">
        <BrandLogo className="h-10" />
        <div className="pn-card rounded-3xl w-full max-w-md p-7 mt-8 pn-fade">
          {err ? (
            <div className="text-center"><AlertTriangle className="mx-auto mb-3" style={{ color: "var(--warn)" }} size={34} /><p className="pn-ink font-semibold">{err}</p>
              <p className="pn-sub text-sm mt-2">Escríbenos a {BRAND.email} o al WhatsApp {BRAND.phone}.</p></div>
          ) : !a ? (
            <div className="flex items-center justify-center gap-2 pn-muted py-6"><Loader2 className="pn-spin" size={18} /> Cargando su cita…</div>
          ) : done ? (
            <div className="text-center">
              <CheckCircle2 className="mx-auto mb-3" style={{ color: "var(--ok)" }} size={40} />
              <h1 className="text-xl font-bold pn-ink">{done === "confirmada" ? "¡Cita confirmada!" : "Solicitud recibida"}</h1>
              <p className="pn-sub text-sm mt-2">{done === "confirmada" ? `Le esperamos el ${prettyDate(a.fecha, true)}${a.hora ? ` a las ${a.hora}` : ""}.` : "El consultorio le contactará para coordinar la nueva fecha."}</p>
            </div>
          ) : (
            <>
              <p className="text-[11px] font-bold uppercase tracking-[0.25em] pn-brand mb-1">{a.brand}</p>
              <h1 className="text-xl font-bold pn-ink">Hola, {a.nombre.split(" ")[0]}</h1>
              <p className="pn-sub text-sm mb-5">Su próxima consulta con {a.doctor}:</p>
              <div className="pn-soft-accent rounded-2xl p-4 mb-5">
                <div className="flex items-center gap-2 font-bold text-lg pn-ink"><CalendarClock size={19} className="pn-accent" /> {prettyDate(a.fecha, true)}{a.hora ? ` · ${a.hora}` : ""}</div>
                {a.servicio && <div className="text-sm pn-sub mt-1">{a.servicio}</div>}
                <div className="text-xs pn-sub mt-2 flex gap-1"><MapPin size={13} className="shrink-0 mt-0.5" /> {a.consulta_lugar || BRAND.address}</div>
              </div>
              {a.status === "confirmada" && <p className="text-sm mb-4" style={{ color: "var(--ok)" }}>Usted ya confirmó esta cita. ¡Gracias!</p>}
              {a.status === "reprogramar" && <p className="text-sm mb-4" style={{ color: "var(--warn)" }}>Ya pidió reprogramar{a.requested_fecha ? ` para el ${prettyDate(a.requested_fecha)}` : ""}. El consultorio le contactará.</p>}
              {mode !== "reschedule" ? (
                <div className="grid gap-2">
                  <button disabled={busy} onClick={() => respond("confirm")} className="pn-btn pn-btn-primary py-3">{busy ? <Loader2 size={16} className="pn-spin" /> : <CheckCircle2 size={17} />} Confirmar asistencia</button>
                  <button disabled={busy} onClick={() => setMode("reschedule")} className="pn-btn pn-btn-ghost py-3">Necesito otra fecha</button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div><label className="pn-label">¿Qué fecha le conviene?</label><input type="date" min={todayISO()} value={fecha} onChange={(e) => setFecha(e.target.value)} className="pn-field" /></div>
                  <div><label className="pn-label">Comentario (opcional)</label><textarea rows={2} value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Ej. prefiero en la tarde" className="pn-field" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => setMode(null)} className="pn-btn pn-btn-ghost">Atrás</button>
                    <button disabled={busy} onClick={() => respond("reschedule")} className="pn-btn pn-btn-primary">{busy ? <Loader2 size={16} className="pn-spin" /> : null} Enviar solicitud</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <p className="text-xs pn-muted mt-6 text-center max-w-sm">{BRAND.name} · {BRAND.phone} · {BRAND.email}</p>
      </div>
    </PanelPage>
  );
}
